import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { PasswordInput } from "@/pages/Register";
import GoogleSignInButton from "@/components/locanest/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      // Prefer ?next= query param, then location.state.from, then role default.
      const params = new URLSearchParams(location.search);
      const nextParam = params.get("next");
      const to = nextParam || location.state?.from || (user.role === "admin" ? "/admin" : user.role === "owner" ? "/owner" : "/listings");
      navigate(to);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Welcome back</div>
      <h1 className="font-display text-4xl mt-2">Login to LocaNest</h1>
      <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
        <div>
          <Label>Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div>
          <Label>Password</Label>
          <PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password" />
          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-xs text-[#C85A32] font-medium hover:underline" data-testid="login-forgot-password">
              Forgot password?
            </Link>
          </div>
        </div>
        {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
        <Button type="submit" disabled={loading} className="w-full bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md h-11" data-testid="login-submit">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <div className="mt-6 text-sm text-[#5C6B7A]">
        New to LocaNest? <Link to="/register" className="text-[#C85A32] font-medium">Create an account</Link>
      </div>

      <div className="mt-6 flex items-center gap-3 text-xs text-[#5C6B7A]"><span className="flex-1 h-px bg-[#1A1F2C]/10" /> or <span className="flex-1 h-px bg-[#1A1F2C]/10" /></div>
      <div className="mt-4"><GoogleSignInButton label="Sign in with Google" testId="google-signin-btn" /></div>
      <div className="mt-2 text-[11px] text-[#5C6B7A]">New here? Google sign-in will ask whether you&rsquo;re a tenant or an owner.</div>
    </div>
  );
}
